import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from '../../../environment/environment';

@Injectable({
  providedIn: 'root',
})
export class TitleService {
  // test / test-local intentionally mirror production: no environment marker in
  // the favicon or the tab title. Only dev keeps a visible marker.
  private readonly ENV_PREFIXES = {
    'prod': '',
    'test': '',
    'test-local': '',
    'dev': '🛠️ ',
  };

  private readonly ENV_SUFFIXES = {
    'prod': '',
    'test': 'test',
    'test-local': 'test-local',
    'dev': 'dev',
  };

  public static readonly APP_NAME = 'Cesmon';

  private readonly originalSetTitle: (title: string) => void;
  private isSettingTitle = false;

  constructor(
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.originalSetTitle = this.titleService.setTitle.bind(this.titleService);
    this.replaceSetTitleMethod();
  }

  private replaceSetTitleMethod(): void {
    this.titleService.setTitle = (title: string): void => {
      if (this.isSettingTitle || this.hasEnvironmentPrefix(title)) {
        this.originalSetTitle(title);
        return;
      }

      const newTitle = this.buildTitleWithSuffix(title);

      this.isSettingTitle = true;
      this.originalSetTitle(newTitle);
      this.isSettingTitle = false;
    };
  }

  public init(): void {
    setTimeout(() => {
      const currentTitle = this.titleService.getTitle();

      if (!this.hasEnvironmentPrefix(currentTitle)) {
        this.titleService.setTitle(currentTitle);
      }

      this.updateFavicon();
    }, 100);
  }

  private updateFavicon(): void {
    const envName = environment.name;
    if (envName === 'prod') {
      return;
    }

    const emoji = this.ENV_PREFIXES[envName as keyof typeof this.ENV_PREFIXES].trim();
    if (!emoji) {
      return;
    }

    this.createEmojiBasedFavicon(emoji);
  }

  private createEmojiBasedFavicon(emoji: string): void {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.font = '24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, canvas.width / 2, canvas.height / 2);

    const faviconUrl = canvas.toDataURL('image/png');

    let link = (this.document.querySelector("link[rel*='icon']") as HTMLLinkElement) || this.document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = faviconUrl;

    if (!this.document.querySelector("link[rel*='icon']")) {
      this.document.getElementsByTagName('head')[0]?.appendChild(link);
    }
  }

  public hasEnvironmentPrefix(title: string): boolean {
    const allPrefixes = Object.values(this.ENV_PREFIXES);
    return allPrefixes.some(prefix => prefix && title.startsWith(prefix));
  }

  public getCurrentEnvironmentPrefix(): string {
    return this.getEnvironmentPrefix(environment.name);
  }

  public getEnvironmentPrefix(envName: string): string {
    return envName in this.ENV_PREFIXES ? this.ENV_PREFIXES[envName as keyof typeof this.ENV_PREFIXES] : this.ENV_PREFIXES.dev;
  }

  public getCurrentEnvironmentSuffix(): string {
    return this.getEnvironmentSuffix(environment.name);
  }

  public getEnvironmentSuffix(envName: string): string {
    return this.ENV_SUFFIXES[envName as keyof typeof this.ENV_SUFFIXES] || this.ENV_SUFFIXES.dev;
  }

  public toSuperscript(text: string): string {
    if (!text) return '';

    return text
      .toLowerCase()
      .split('')
      .map(char => this.SUPERSCRIPT_MAP[char] ?? char)
      .join('');
  }

  public getCurrentEnvironment(): string {
    return environment.name;
  }

  public buildTitleWithPrefix(baseTitle: string): string {
    const envPrefix = this.getCurrentEnvironmentPrefix();
    return `${envPrefix}${TitleService.APP_NAME} - ${baseTitle}`;
  }

  public buildTitleWithPrefixAndSuffix(baseTitle: string): string {
    const envPrefix = this.getCurrentEnvironmentPrefix();
    const envSuffix = this.getCurrentEnvironmentSuffix();
    const superscriptSuffix = this.toSuperscript(envSuffix);

    if (environment.name === 'prod') {
      return `${envPrefix}${TitleService.APP_NAME} - ${baseTitle}`;
    }

    return `${envPrefix}${TitleService.APP_NAME}${superscriptSuffix} - ${baseTitle}`;
  }

  public buildTitleWithSuffix(baseTitle: string): string {
    const envSuffix = this.getCurrentEnvironmentSuffix();
    const superscriptSuffix = this.toSuperscript(envSuffix);

    if (environment.name === 'prod') {
      return `${TitleService.APP_NAME} - ${baseTitle}`;
    }

    return `${TitleService.APP_NAME}${superscriptSuffix} - ${baseTitle}`;
  }

  public setSimpleTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  public setTitleWithCustomPrefix(title: string, customPrefix: string): void {
    this.isSettingTitle = true;
    this.originalSetTitle(`${customPrefix}${TitleService.APP_NAME} - ${title}`);
    this.isSettingTitle = false;
  }

  public setTitleWithCustomPrefixAndSuffix(title: string, customPrefix: string, customSuffix: string): void {
    this.isSettingTitle = true;
    const superscriptSuffix = this.toSuperscript(customSuffix);
    this.originalSetTitle(`${customPrefix}${TitleService.APP_NAME}${superscriptSuffix} - ${title}`);
    this.isSettingTitle = false;
  }

  public setCustomFavicon(emoji: string): void {
    this.createEmojiBasedFavicon(emoji);
  }

  private readonly SUPERSCRIPT_MAP: { [key: string]: string } = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    'a': 'ᵃ',
    'b': 'ᵇ',
    'c': 'ᶜ',
    'd': 'ᵈ',
    'e': 'ᵉ',
    'f': 'ᶠ',
    'g': 'ᵍ',
    'h': 'ʰ',
    'i': 'ⁱ',
    'j': 'ʲ',
    'k': 'ᵏ',
    'l': 'ˡ',
    'm': 'ᵐ',
    'n': 'ⁿ',
    'o': 'ᵒ',
    'p': 'ᵖ',
    'q': 'ᑫ',
    'r': 'ʳ',
    's': 'ˢ',
    't': 'ᵗ',
    'u': 'ᵘ',
    'v': 'ᵛ',
    'w': 'ʷ',
    'x': 'ˣ',
    'y': 'ʸ',
    'z': 'ᶻ',
    '(': '⁽',
    ')': '⁾',
    '+': '⁺',
    '-': '⁻',
    '=': '⁼',
    ' ': ' ',
  };
}
